import { PlusIcon } from '@phosphor-icons/react';
import { ClipboardIcon, CopyIcon, TrashIcon, XIcon } from '@phosphor-icons/react';
import { createId } from '@paralleldrive/cuid2';
import { useReducer, useState } from 'react';
import toast from 'react-hot-toast';
import { IterableElement } from 'type-fest';

import { Button } from '../../../../components/button/Button';
import { Input } from '../../../../components/input/Input';
import { Day as DayEnum } from 'src/types/enums';
import { updateOpeningHours } from '../../models';
import type { UpdateOpeningHoursItem } from '../../endpoints/UpdateOpeningHoursEndpoint.types';
import { formatInputTime, parseInputTime } from '../../../../utils/date';
import { getDisplayError } from '../../../../utils/get-display-error';
import { Location } from '../Location';
import { useLocation } from '../Location/locationContext';
import { captureException } from '@sentry/react';

type LocationOpeningTime = IterableElement<Location['openingTimes']>;

type EntryMap = Record<DayEnum, OpeningHourEntry[]>;

type OpeningHoursAction =
  | {
      type: 'change';
      day: DayEnum;
      index: number;
      opening: string;
      closing: string;
    }
  | {
      type: 'add';
      day: DayEnum;
    }
  | {
      type: 'delete';
      day: DayEnum;
      id: string;
    }
  | {
      type: 'validated';
      value: EntryMap;
    }
  | {
      type: 'copy';
      day: DayEnum;
    }
  | {
      type: 'paste';
      day: DayEnum;
    }
  | {
      type: 'cancel-copy';
    };

interface OpeningHourEntry {
  id: string;
  opening: string;
  closing: string;
  error: string;
}

interface OpeningHoursState {
  days: EntryMap;
  copying?: DayEnum;
}

function getInitialState(openingHours: LocationOpeningTime[]): OpeningHoursState {
  const days: EntryMap = {
    [DayEnum.Monday]: [],
    [DayEnum.Tuesday]: [],
    [DayEnum.Wednesday]: [],
    [DayEnum.Thursday]: [],
    [DayEnum.Friday]: [],
    [DayEnum.Saturday]: [],
    [DayEnum.Sunday]: [],
  };

  for (const entry of openingHours) {
    days[entry.day].push({
      id: createId(),
      opening: formatInputTime(entry.opening),
      closing: formatInputTime(entry.closing),
      error: '',
    });
  }

  return { days };
}

function validateValues(values: EntryMap): { days: EntryMap; hasError: boolean } {
  const days: EntryMap = { ...values };
  let hasError = false;
  for (const [key, value] of Object.entries(days)) {
    const sortedValue: OpeningHourEntry[] = value
      .filter((val) => parseInputTime(val.closing) - parseInputTime(val.opening) > 0)
      .sort((a, b) => parseInputTime(a.opening) - parseInputTime(b.opening));

    for (let i = 1; i < sortedValue.length; i++) {
      const prevValue = sortedValue[i - 1];
      const currValue = sortedValue[i];
      if (parseInputTime(currValue.opening) < parseInputTime(prevValue.closing)) {
        prevValue.error = 'Openingstijd valt voor vorige sluitingstijd';
        hasError = true;
      }
    }

    // @ts-ignore
    days[key] = sortedValue;
  }
  return { days, hasError };
}

function openingHoursReducer(state: OpeningHoursState, action: OpeningHoursAction) {
  switch (action.type) {
    case 'add':
      let times = state.days[action.day];
      times = times.filter((val) => parseInputTime(val.closing) - parseInputTime(val.opening) > 0);
      times.push({
        id: createId(),
        opening: '00:00',
        closing: '00:00',
        error: '',
      });
      state.days[action.day] = times;
      return { days: state.days };
    case 'change':
      const foundValue = state.days[action.day][action.index];
      foundValue.opening = action.opening;
      foundValue.closing = action.closing;
      return { days: state.days };
    case 'delete':
      let items = state.days[action.day];
      state.days[action.day] = items.filter((i) => i.id !== action.id);
      return { days: state.days };
    case 'validated':
      return {
        days: action.value,
      };
    case 'copy':
      return {
        ...state,
        copying: action.day,
      };
    case 'paste':
      if (!state.copying) {
        return state;
      }

      const days = { ...state.days };
      days[action.day] = days[state.copying].map((v) => {
        return {
          ...v,
          id: createId(),
        };
      });
      return {
        ...state,
        days,
      };
    case 'cancel-copy':
      return {
        ...state,
        copying: undefined,
      };
    default:
      return state;
  }
}

function flattenDays(days: EntryMap): UpdateOpeningHoursItem[] {
  const values: UpdateOpeningHoursItem[] = [];
  for (const [key, value] of Object.entries(days)) {
    for (const entry of value) {
      values.push({
        day: key as DayEnum,
        opening: parseInputTime(entry.opening),
        closing: parseInputTime(entry.closing),
      });
    }
  }
  return values;
}

const LocationOpeningHours = () => {
  const { location } = useLocation();
  const [data, dispatch] = useReducer(openingHoursReducer, getInitialState(location.openingTimes));
  const [isLoading, setIsLoading] = useState(false);

  const handleSubmit = async () => {
    setIsLoading(true);
    try {
      const { days, hasError } = validateValues(data.days);
      dispatch({
        type: 'validated',
        value: days,
      });

      if (hasError) {
        throw new Error('Validatie fout');
      }

      const flattened = flattenDays(days);
      await updateOpeningHours(location.id, flattened);

      toast.success('Openingsuren aangepast');
    } catch (err) {
      captureException(err);
      toast.error('Kon openingsuren niet aanpassen: ' + getDisplayError(err));
    }
    setIsLoading(false);
  };

  return (
    <div className="flex flex-col gap-4">
      <div className="card">
        {Object.entries(data.days).map(([day, time]) => {
          return (
            <div className="flex items-center gap-3 border-b border-border px-5 py-3 last:border-b-0" key={day}>
              <div className="flex w-[220px] shrink-0 flex-col gap-0.5">
                <div className="text-sm font-semibold text-fg">{day}</div>
                <div className="text-sm text-fg-secondary">
                  {time.length === 0 ? 'Gesloten' : `${time.length} ${time.length === 1 ? 'tijdvak' : 'tijdvakken'}`}
                </div>
              </div>

              <div className="flex shrink-0 gap-1.5">
                {!data.copying && (
                  <Button
                    size={8}
                    ariaLabel={`Kopieer openingsuren van ${day}`}
                    onTrigger={async () => {
                      dispatch({
                        type: 'copy',
                        day: day as DayEnum,
                      });
                    }}
                    isDisabled={isLoading}
                  >
                    <CopyIcon className="button-icon" />
                  </Button>
                )}
                {data.copying && data.copying !== day && (
                  <Button
                    size={8}
                    ariaLabel={`Plak openingsuren op ${day}`}
                    onTrigger={async () => {
                      dispatch({
                        type: 'paste',
                        day: day as DayEnum,
                      });
                    }}
                    isDisabled={isLoading}
                  >
                    <ClipboardIcon className="button-icon" />
                  </Button>
                )}
                {data.copying && data.copying === day && (
                  <Button
                    size={8}
                    ariaLabel="Annuleer kopiëren"
                    onTrigger={async () => {
                      dispatch({
                        type: 'cancel-copy',
                      });
                    }}
                    color="primary"
                    isDisabled={isLoading}
                  >
                    <XIcon className="button-icon" />
                  </Button>
                )}
                <Button
                  size={8}
                  ariaLabel={`Voeg tijdvak toe aan ${day}`}
                  onTrigger={() => {
                    dispatch({
                      type: 'add',
                      day: day as DayEnum,
                    });
                  }}
                  isDisabled={isLoading}
                >
                  <PlusIcon className="button-icon" />
                </Button>
              </div>

              <div className="flex min-w-0 flex-1 flex-col gap-2">
                {time.map((val, idx) => {
                  return (
                    <div key={`${day}-${val.id}`}>
                      <div className="flex items-center gap-2">
                        <div className="w-32">
                          <Input
                            type="time"
                            value={val.opening}
                            onChange={(newValue) => {
                              dispatch({
                                type: 'change',
                                day: day as DayEnum,
                                index: idx,
                                opening: newValue,
                                closing: val.closing,
                              });
                            }}
                            isDisabled={isLoading}
                          />
                        </div>
                        <div className="w-32">
                          <Input
                            type="time"
                            value={val.closing}
                            onChange={(newValue) => {
                              dispatch({
                                type: 'change',
                                day: day as DayEnum,
                                index: idx,
                                opening: val.opening,
                                closing: newValue,
                              });
                            }}
                            isDisabled={isLoading}
                          />
                        </div>
                        <Button
                          color="danger"
                          size={8}
                          ariaLabel="Verwijder tijdvak"
                          onTrigger={() => {
                            dispatch({
                              type: 'delete',
                              day: day as DayEnum,
                              id: val.id,
                            });
                          }}
                          isDisabled={isLoading}
                        >
                          <TrashIcon className="button-icon" />
                        </Button>
                      </div>

                      {!!val.error && <div className="mt-1 text-xs text-negative-text">{val.error}</div>}
                    </div>
                  );
                })}
              </div>
            </div>
          );
        })}
      </div>

      <div>
        <Button
          color="primary"
          iconLeft={<PlusIcon className="button-icon" />}
          onTrigger={handleSubmit}
          isLoading={isLoading}
          isDisabled={isLoading}
        >
          Pas openingsuren aan
        </Button>
      </div>
    </div>
  );
};

export default LocationOpeningHours;
